import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { formatApiErrorDetail, setAccessToken, setRefreshToken } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export default function AccountSecurityPanel() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [check, setCheck] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkServers = async () => {
    setCheck({ running: true });
    const fps = new Set();
    const instances = new Set();
    try {
      for (let i = 0; i < 20; i += 1) {
        const { data } = await api.get("/admin/security/status", { params: { n: i } });
        fps.add(data.jwt_fingerprint);
        instances.add(data.instance);
      }
      setCheck({ fps: [...fps], instances: [...instances] });
    } catch (err) {
      setCheck({ error: formatApiErrorDetail(err.response?.data?.detail) || "Check failed." });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) return setError("New passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/account/password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setForm(EMPTY);
      toast.success("Password changed. All other admin sessions were signed out.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 rounded-xl border border-ink-100 bg-white p-6 shadow-sm" data-testid="admin-security-panel">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-brand" />
        <h3 className="h3">Change admin password</h3>
      </div>
      <p className="mt-1 text-sm text-ink-500">
        14+ characters, three of: lowercase, uppercase, digits, symbols. Changing it signs out every other admin session.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {[
          ["current_password", "Current password", "current-password"],
          ["new_password", "New password", "new-password"],
          ["confirm", "Confirm new password", "new-password"],
        ].map(([k, label, ac]) => (
          <div key={k}>
            <Label htmlFor={`sec-${k}`}>{label}</Label>
            <Input id={`sec-${k}`} type="password" autoComplete={ac} required value={form[k]} onChange={set(k)} data-testid={`admin-security-${k}-input`} />
          </div>
        ))}
      </div>
      {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-security-error">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy} data-testid="admin-security-submit-btn">
          {busy ? "Changing…" : "Change password"}
        </Button>
        <Button type="button" variant="outline" onClick={checkServers} disabled={check?.running} data-testid="admin-security-check-servers-btn">
          {check?.running ? "Checking…" : "Check signing key on all servers"}
        </Button>
      </div>
      {check && !check.running && (
        <p className="mt-3 text-sm" data-testid="admin-security-check-result">
          {check.error
            ? check.error
            : check.fps.length === 1
              ? `OK — one signing key (${check.fps[0]}) across ${check.instances.length} server(s).`
              : `MISMATCH — ${check.fps.length} different signing keys seen. Stop and report.`}
        </p>
      )}
    </form>
  );
}
